import { FC, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { Toolbar } from '@/components/ui/toolbar/ToolBar';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { useTypedSelector, useTypedDispatch } from '@/store/hooks';
import { creatorActions } from '@/store/slices/creatorSlice';
import { Box, LoadingOverlay } from '@mantine/core';
import {
	useElementSize,
	useFullscreenElement,
	useMediaQuery,
	useMergedRef,
} from '@mantine/hooks';
import { ToolbarButton } from '../partials/toolbarButton/ToolbarButton';
import { InstructionModal } from '../partials/instructionModal/InstructionModal';
import { roundToHalf } from '@/utils/utilities';
import { Legend } from '@/components/ui/legend/Legend';
import { ModuleContextMenu } from '../partials/moduleContextMenu/ModuleContextMenu';
import { IconX } from '@tabler/icons-react';
import styles from './ProductPreview.module.scss';
import APP_CONFIG from '@/configs/appConfig';

const renderer = ProjectRenderer.getInstance();

export const ProductPreview: FC = () => {
	const canvasRef = useRef<HTMLCanvasElement>(null!);

	const isMobile = useMediaQuery(
		`(max-width: ${APP_CONFIG.BREAKPOINTS.MD})`,
		false,
		{
			getInitialValueInEffect: false,
		}
	);

	const { renderer: rendererSlice } = useTypedSelector(
		(state) => state.creator
	);

	const dispatch = useTypedDispatch();

	const { ref: resizeRef, width } = useElementSize();
	const {
		ref: fullscreenRef,
		toggle: toggleFullscreen,
		fullscreen,
	} = useFullscreenElement();
	const mergedRef = useMergedRef(resizeRef, fullscreenRef);

	useLayoutEffect(() => {
		const canvas = canvasRef.current;
		renderer.init(canvas, isMobile);
	}, []);

	useEffect(() => {
		renderer.handleResize();
	}, [width, fullscreen]);

	const zoomEnabled = useMemo(
		() => ({
			in: rendererSlice.cameraDistance > RENDERER_CONFIG.CONTROLS.MIN_DISTANCE,
			out: rendererSlice.cameraDistance < RENDERER_CONFIG.CONTROLS.MAX_DISTANCE,
		}),
		[rendererSlice.cameraDistance]
	);

	const handleZoom = (zoomAction: 'in' | 'out') => () => {
		if (!renderer) return;

		let cameraDistance =
			rendererSlice.cameraDistance +
			(zoomAction === 'in'
				? -RENDERER_CONFIG.CAMERA_DISTANCE_TICK
				: RENDERER_CONFIG.CAMERA_DISTANCE_TICK);

		cameraDistance = Math.max(
			RENDERER_CONFIG.CONTROLS.MIN_DISTANCE,
			Math.min(cameraDistance, RENDERER_CONFIG.CONTROLS.MAX_DISTANCE)
		);

		cameraDistance = roundToHalf(cameraDistance);

		dispatch(
			creatorActions.updateRenderer({
				cameraDistance,
			})
		);

		renderer.handleCameraZoom(cameraDistance);
	};

	const handleCenterView = () => {
		renderer?.handleCameraCenter();
	};

	return (
		<Box component="main" className={styles.container}>
			<LoadingOverlay visible={rendererSlice.loading} />
			<Box ref={mergedRef} className={styles.fullscreenWrapper}>
				{fullscreen && (
					<ToolbarButton
						Icon={IconX}
						onClick={toggleFullscreen}
						className={styles.closeFullScreenBtn}
					/>
				)}
				<Box
					ref={canvasRef}
					component="canvas"
					onDragStart={(e) => e.preventDefault()}
					onContextMenu={(e) => e.preventDefault()}
					onTouchStart={(e) => e.preventDefault()}
				></Box>
			</Box>
			<Legend />
			<Toolbar
				zoomEnabled={zoomEnabled}
				handleZoom={handleZoom}
				toggleFullscreen={toggleFullscreen}
				fullscreen={fullscreen}
				handleCenterView={handleCenterView}
			/>
			<InstructionModal />
			<ModuleContextMenu />
		</Box>
	);
};
